/**
 * sunburstPipeline — radiating sunburst with morphing rays.
 *
 * A central bright point with N rays extending outward. Each ray's
 * length and width is modulated by audio (bass drives ray count,
 * mid drives ray amplitude, high drives color shift).
 *
 * The rays radiate from a single point in polar coordinates. We also
 * add a subtle spiral distortion that makes the sunburst feel alive.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SunburstPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  // Polar coords.
  float r = length(p);
  float theta = atan(p.y, p.x);
  // Slow rotation.
  theta -= uTime * 0.2;

  // Ray count scales with audio.
  const float PI = 3.14159265;
  const float TWO_PI = 6.2831853;
  float rayCount = 16.0 + bass * 16.0;
  // Subdivide the full circle into rayCount slices.
  float sliceAngle = TWO_PI / rayCount;
  // Find the angular position within the current slice.
  float slicePhase = mod(theta, sliceAngle) / sliceAngle;  // 0..1
  // Distance to the nearest ray centerline.
  float distToRay = abs(slicePhase - 0.5);
  // Audio: ray width.
  float rayWidth = 0.08 + high * 0.05;
  // Ray intensity falls off with distance from centerline.
  float rayIntensity = smoothstep(rayWidth, 0.0, distToRay);
  // Audio: ray length modulated by mid (sin wave per ray).
  float rayLen = 0.6 + 0.3 * sin(theta * rayCount * 0.5 + uTime * 2.0) + 0.2 * mid;
  // Falloff with radius (rays fade out at distance).
  rayIntensity *= smoothstep(rayLen + 0.1, rayLen - 0.1, r);

  // Color: warm yellow at the center, fading to orange at edges.
  vec3 hot = vec3(1.0, 0.85, 0.30);
  vec3 cool = vec3(0.95, 0.30, 0.10);
  vec3 rayColor = mix(hot, cool, smoothstep(0.0, rayLen, r));
  // Audio: shift color on high.
  rayColor = mix(rayColor, vec3(1.0, 0.4, 0.85), high * 0.3);

  // Background: deep space.
  vec3 bg = vec3(0.03, 0.05, 0.10);
  // Subtle nebula tint.
  bg += vec3(0.08, 0.04, 0.18) * exp(-r * 1.0) * 0.5;

  // Central bright core.
  float core = exp(-r * r * 50.0);
  vec3 coreColor = vec3(1.0, 0.95, 0.80);

  // Combine.
  vec3 col = bg + rayColor * rayIntensity * (0.5 + bass * 0.5);
  col += coreColor * core * (1.0 + high * 0.5);

  // Halo around the core.
  col += vec3(1.0, 0.7, 0.3) * exp(-r * 4.0) * 0.3 * (1.0 + bass * 0.3);

  // Vignette.
  float vig = 1.0 - r * r * 0.2;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeSunburstBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeSunburstRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSunburstPipeline(gl: WebGL2RenderingContext): SunburstPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSunburst(
  gl: WebGL2RenderingContext,
  pipe: SunburstPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
